/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

/* eslint react/prefer-stateless-function:off */
import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import AnalyticsConstants from "app/constants/AnalyticsConstants";
import {
    Dialog, Heading, Divider, Content, ButtonGroup, Button, Link, useDialogContainer,
} from "@adobe/react-spectrum";
import ServiceRegistry from "bundle/util/ServiceRegistry";
import { getEmbedApiVersion } from "app/util/AppUtil";

import LegalDialogCss from "./LegalDialog.css";

function LegalDialog(props) {
    const dialog = props.isOutsideContainer ? {
        dismiss: () => {},
    } : useDialogContainer();
    const _appStore = ServiceRegistry.getService("AppStore");
    let version = getEmbedApiVersion();

    const applicationVersion = _appStore.getConfig().applicationVersion;
    if (applicationVersion) {
        version = `${applicationVersion}_${version}`;
    }

    const title = _appStore.intl.formatMessage({ id: "LEGAL_NOTICES" });
    const confirmLabel = _appStore.intl.formatMessage({ id: "LEGAL_CONFIRM_LABEL" });

    const locale = _appStore.intl.locale;
    const [lang, territory] = locale.toLowerCase().split("-");
    let termUrl = "https://www.adobe.com/go/terms";
    let cookiesUrl = "https://www.adobe.com/go/privacy/cookies";
    let privacyUrl = "https://www.adobe.com/go/privacy_policy";
    let poweredByUrl = "https://acrobat.adobe.com/us/en/";
    const doNotSellByUrl = "https://www.adobe.com/privacy/us-rights.html";
    if (lang !== "en") {
        termUrl = `https://www.adobe.com/go/terms_${territory}`;
        cookiesUrl = `https://www.adobe.com/go/privacy/cookies_${territory}`;
        privacyUrl = `https://www.adobe.com/go/privacy_policy_${territory}`;
        poweredByUrl = `https://acrobat.adobe.com/${territory}/${lang}/`;
    }

    const divStyle = {};
    if (window.innerWidth < 750) {
        divStyle.width = window.innerWidth - 120;
    }
    let legalDialogClass = LegalDialogCss.legalDialog;
    if (_appStore.isSizedPreset && !_appStore.inFullScreen) {
        legalDialogClass = "";
    }
    const dialogClasses = [legalDialogClass];
    if (_appStore.isInLinePreset) {
        let top = _appStore.heightInView.viewStartH + 400;
        const totalHeight = _appStore.documentViewUpdate.scrollHeight;
        if (totalHeight > 5000) {
            top += Math.floor(20 * ((totalHeight - 5000) / 1000));
        }
        if (top + 400 > totalHeight) {
            top -= 300;
        }
        document.documentElement.style.setProperty("--in_line_legal_height", `${top}px`);
        dialogClasses.push(LegalDialogCss.legalDialogInline);
    }
    let onConfirm = props.onConfirm;
    let dialogClosed = false;
    if (_appStore.inFullScreen) {
        onConfirm = () => {
            dialogClosed = true;
            props.onConfirm();
        };
        const tryToClose = () => {
            if (!_appStore.inFullScreen) {
                props.onConfirm();
            } else if (!dialogClosed) {
                setTimeout(tryToClose, 100);
            }
        };
        tryToClose();
    }
    const startYear = _appStore.getConfig().releaseYear;
    const currentYear = new Date().getFullYear();
    let yearString = startYear;
    if (parseInt(startYear, 10) !== currentYear) {
        yearString = `${startYear} - ${currentYear}`;
    }
    const analyticsProviderService = ServiceRegistry.getService("AnalyticsProviderService");
    const enableCMP = !(_appStore.getConfig().disableCMPTool)
        && window.adobePrivacy
        && window.adobePrivacy.showConsentPopup
        && typeof window.adobePrivacy.showConsentPopup === "function";
    return (
        <Dialog UNSAFE_className={ classNames(dialogClasses) }>
            <Heading>{ title }</Heading>
            <Divider />
            <Content>
                <div className={ LegalDialogCss.contentSection } style={ divStyle }>
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <Link
                        UNSAFE_className={ classNames([LegalDialogCss.boldLink]) }
                        onPress={ () => {
                            analyticsProviderService.sendEvent(AnalyticsConstants.LEGAL_NOTICES_TERMS);
                        } }
                        isQuiet
                    >
                        <a
                            href={ termUrl }
                            target="_blank"
                            rel="noreferrer"
                        >
                            {_appStore.intl.formatMessage({ id: "LEGAL_TERMS_OF_USE" })}
                        </a>
                    </Link>
                    <br />
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <Link
                        onPress={
                            () => {
                                analyticsProviderService.sendEvent(AnalyticsConstants.LEGAL_NOTICES_PRIVACY);
                            }
                        }
                        UNSAFE_className={ classNames([LegalDialogCss.boldLink]) }
                        isQuiet
                    >
                        <a
                            href={ privacyUrl }
                            target="_blank"
                            rel="noreferrer"
                        >
                            {_appStore.intl.formatMessage({ id: "LEGAL_PRIVACY_POLICY" })}
                        </a>
                    </Link>
                    <br />
                    {
                        enableCMP
                        && (
                            // eslint-disable-next-line jsx-a11y/anchor-is-valid
                            <Link
                                onPress={
                                    event => {
                                        // eslint-disable-next-line max-len
                                        analyticsProviderService.sendEvent(AnalyticsConstants.LEGAL_NOTICES_CMP);
                                        onConfirm();
                                        window.adobePrivacy.showConsentPopup();
                                        event.preventDefault();
                                    }
                                }
                                // eslint-disable-next-line max-len
                                UNSAFE_className={ classNames([LegalDialogCss.boldLink]) }
                                isQuiet
                            >
                                <a
                                    href="/"
                                >
                                    {_appStore.intl.formatMessage({ id: "LEGAL_COOKIE_PREFERENCES" })}
                                </a>
                            </Link>
                        )
                    }
                    {
                        !enableCMP
                        && (
                            // eslint-disable-next-line jsx-a11y/anchor-is-valid
                            <Link
                                onPress={
                                    () => {
                                        // eslint-disable-next-line max-len
                                        analyticsProviderService.sendEvent(AnalyticsConstants.LEGAL_NOTICES_COOKIES);
                                    }
                                }
                                // eslint-disable-next-line max-len
                                UNSAFE_className={ classNames([LegalDialogCss.boldLink]) }
                                isQuiet
                            >
                                <a
                                    href={ cookiesUrl }
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {_appStore.intl.formatMessage({ id: "LEGAL_COOKIE_PREFERENCES" })}
                                </a>
                            </Link>
                        )
                    }
                    <br />
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <Link
                        onPress={
                            () => {
                                analyticsProviderService.sendEvent(AnalyticsConstants.LEGAL_NOTICES_DO_NOT_SELL);
                            }
                        }
                        UNSAFE_className={ classNames([LegalDialogCss.boldLink]) }
                        isQuiet
                    >
                        <a
                            href={ doNotSellByUrl }
                            target="_blank"
                            rel="noreferrer"
                        >
                            {_appStore.intl.formatMessage({ id: "LEGAL_DO_NOT_SELL" })}
                        </a>
                    </Link>
                    <br />
                    <p>
                        { `© ${yearString} Adobe. All rights reserved.` }
                    </p>
                    <p>
                        Adobe, the Adobe logo, and the Adobe PDF logo are either
                        registered trademarks or trademarks of Adobe in the United States
                        and/or other countries. All other trademarks are the property of
                        their respective owners.
                    </p>
                    <p>
                        Third Party notices, terms and conditions pertaining to
                        third party software can be found at&nbsp;
                        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                        <Link
                            onPress={
                                () => {
                                    // eslint-disable-next-line max-len
                                    analyticsProviderService.sendEvent(AnalyticsConstants.LEGAL_NOTICES_THIRD_PARTY);
                                }
                            }
                            isQuiet
                        >
                            <a
                                href="https://www.adobe.com/go/thirdparty"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                https://www.adobe.com/go/thirdparty
                            </a>
                        </Link>
                        {" "}
                        and are incorporated by reference.
                    </p>
                </div>
                { version
                    ? (
                        <div className={ LegalDialogCss.versionSection }>
                            { _appStore.intl.formatMessage({ id: "LEGAL_APP_VERSION" }) }
                            <div>{version}</div>
                        </div>
                    ) : null}
                <div className={ LegalDialogCss.poweredBy }>
                    { _appStore.intl.formatMessage({ id: "LEGAL_POWERED_BY" }) }
                    &nbsp;
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <Link
                        onPress={
                            () => {
                                analyticsProviderService.sendEvent(AnalyticsConstants.LEGAL_NOTICES_POWERED_BY);
                            }
                        }
                        isQuiet
                    >
                        <a
                            href={ poweredByUrl }
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Adobe Document Cloud
                        </a>
                    </Link>
                    .
                </div>
            </Content>
            <ButtonGroup>
                <Button
                    variant="secondary"
                    onPress={ () => { onConfirm(); dialog.dismiss(); } }
                    UNSAFE_className={ LegalDialogCss.cursor_pointer }
                    autoFocus
                >
                    { confirmLabel }
                </Button>
            </ButtonGroup>
        </Dialog>
    );
}

LegalDialog.defaultProps = {
    isOutsideContainer: false,
};

LegalDialog.propTypes = {
    onConfirm: PropTypes.func.isRequired,
    isOutsideContainer: PropTypes.bool,
};

export default LegalDialog;
